import { useMemo, useState } from 'react'
import { Cell, Column } from 'react-table'
import { Button, Radio, RadioGroup, Text, useDisclosure } from '@ritualco/jutsu'
import { BasicTable } from 'components/BasicTable'
import { useContent } from 'lib/content/react'
import { PaymentMethodRow } from 'types/entities/PaymentMethod'
import { PaymentMethodDeleteModal } from 'components/Modals/PaymentMethodDeleteModal'
import { PaymentMethodSwitchModal } from 'components/Modals/PaymentMethodSwitchModal'

const getDisplayMonth = (month: Number) =>
  month < 10 ? `0${month}` : month.toString()

export type PaymentMethodsTableProps = {
  paymentMethods?: PaymentMethodRow[]
  onRemove?: (paymentMethodId: string) => void
  onSwitch?: (paymentMethodId: string) => void
}

export const PaymentMethodsTable = ({
  paymentMethods = [],
  onRemove = () => {},
  onSwitch = () => {},
}: PaymentMethodsTableProps) => {
  const { formatMessage } = useContent()
  const {
    isOpen: isRemoveOpen,
    onOpen: onRemoveOpen,
    onClose: onRemoveClose,
  } = useDisclosure()
  const {
    isOpen: isSwitchOpen,
    onOpen: onSwitchOpen,
    onClose: onSwitchClose,
  } = useDisclosure()
  const [triggeredId, setTriggeredId] = useState('')
  const columns = useMemo<Column<PaymentMethodRow>[]>(
    () => [
      {
        accessor: 'id',
        Cell: ({ row }: Cell<PaymentMethodRow>) => {
          const {
            id,
            brand,
            last4,
            expirationMonth,
            expirationYear,
          } = row.original

          return (
            <Radio value={id} size="lg" spacing={6} position="relative">
              {brand && last4 && expirationMonth && expirationYear ? (
                <>
                  <Text fontSize="sm" fontWeight="medium">
                    {formatMessage(
                      { id: 'pmportal.billing.cardDescription' },
                      { brand, last4 }
                    )}
                  </Text>
                  <Text fontSize="xs" color="grayscale.secondary">
                    {formatMessage(
                      { id: 'pmportal.billing.cardExpiration' },
                      {
                        expirationMonth: getDisplayMonth(expirationMonth),
                        expirationYear,
                      }
                    )}
                  </Text>
                </>
              ) : (
                <Text fontSize="sm" fontWeight="medium">
                  {formatMessage({ id: 'pmportal.billing.unknownCard' })}
                </Text>
              )}
            </Radio>
          )
        },
      },
      {
        id: 'actions',
        Cell: ({ row }: Cell<PaymentMethodRow>) =>
          row.original.isSelected && paymentMethods.length > 1 ? null : (
            <Button
              size="sm"
              colorScheme="transparent"
              variant="outline"
              float="right"
              onClick={() => {
                setTriggeredId(row.original.id)
                onRemoveOpen()
              }}
            >
              {formatMessage({ id: 'pmportal.global.actionRemove' })}
            </Button>
          ),
      },
    ],
    [formatMessage, onRemoveOpen, paymentMethods.length]
  )
  const selected = paymentMethods.find(x => x.isSelected)?.id || ''

  return (
    <>
      <RadioGroup
        onChange={id => {
          // Unfocus previously selected payment method id for bug fix WT-1978
          ;(document.activeElement as HTMLElement)?.blur()
          setTriggeredId(id)
          onSwitchOpen()
        }}
        value={selected}
      >
        <BasicTable
          columns={columns}
          data={paymentMethods}
          headers="none"
          placeholder={formatMessage({ id: 'pmportal.billing.emptyTable' })}
        />
      </RadioGroup>
      <PaymentMethodDeleteModal
        isOpen={isRemoveOpen}
        onClose={onRemoveClose}
        onSubmit={() => onRemove(triggeredId)}
        isDescriptive={paymentMethods.length === 1}
      />
      <PaymentMethodSwitchModal
        isOpen={isSwitchOpen}
        onClose={onSwitchClose}
        onSubmit={() => onSwitch(triggeredId)}
      />
    </>
  )
}
